import React, { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';

type Fmt = (v: number) => string;

// Data labels are sized in CSS px, but the chart's CSS-px space shrinks as the
// browser zooms IN — so fixed-size labels stop fitting and get culled. We scale
// the label font down by the zoom factor (tracked via devicePixelRatio relative
// to mount, so it's independent of the display's base DPR), floored so the text
// never gets unreadable. 0.75 ≈ 133% zoom — the point where the floor takes over
// and culling resumes, matching the "give it room up to ~133%" target.
const BASE_LABEL_PX = 10;
const MIN_LABEL_SCALE = 0.75;

type Props = {
  type?: 'bar' | 'line' | 'area';
  series: { name?: string; data: (number | null)[] }[];
  categories?: (string | number)[];
  height?: number | string;
  horizontal?: boolean;
  valueFormatter?: Fmt;
  /** explicit per-bar colors (use with distributed) or single series color */
  colors?: string[];
  distributed?: boolean;
  /** Stack series on a shared baseline. With one positive + one negative series
   *  and `horizontal`, this yields a diverging/tornado bar (e.g. Long right of
   *  zero, Short left). Defaults false — unchanged for every existing caller. */
  stacked?: boolean;
  sparkline?: boolean;
  style?: React.CSSProperties;
  /** Fires with the clicked bar's index (enables click-to-drill on bars). */
  onBarClick?: (index: number) => void;
};

/**
 * Thin, height-controllable ApexCharts wrapper. The shared chart wrappers
 * hard-code a percent axis / fixed height which fights this view's fixed
 * layout — this lets each panel control height and units so nothing overflows.
 */
const MiniChart: React.FC<Props> = ({
  type = 'bar',
  series,
  categories,
  height = '100%',
  horizontal = false,
  valueFormatter,
  colors,
  distributed = false,
  stacked = false,
  sparkline = false,
  style,
  onBarClick,
}) => {
  // Track browser zoom relative to mount and shrink the label font to match, so
  // labels keep fitting (instead of being culled) when the user zooms in.
  const baseDprRef = useRef(
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
  );
  const [labelScale, setLabelScale] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const recompute = () => {
      const dpr = window.devicePixelRatio || 1;
      // <1 when zoomed in past the mount zoom; clamp so we only ever shrink, and
      // never below the readable floor.
      const next = Math.min(
        1,
        Math.max(MIN_LABEL_SCALE, baseDprRef.current / dpr),
      );
      setLabelScale(prev => (Math.abs(prev - next) > 0.01 ? next : prev));
    };
    recompute();
    // Browser zoom fires a window resize; recompute the scale from the new DPR.
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  // Everything that has to agree with the label's footprint scales off one font
  // size: per-glyph width (fit test), the end/top offsets, and the minimum bar
  // height a vertical label needs. At scale 1 these reproduce the prior 10px /
  // 6px / 18px constants exactly.
  const labelFontPx = BASE_LABEL_PX * labelScale;
  const glyphPx = labelFontPx * 0.6;
  const labelOffsetPx = Math.round(labelFontPx * 0.6);

  const valueAxis = {
    labels: {
      formatter: valueFormatter
        ? (v: number) => valueFormatter(v)
        : (v: number) => `${v}`,
      style: { fontSize: '11px' },
    },
  };
  const catAxis = {
    categories,
    labels: { style: { fontSize: '11px' }, trim: true },
  };

  const options: any = {
    chart: {
      type,
      height,
      stacked,
      parentHeightOffset: 0,
      toolbar: { show: false },
      animations: { enabled: false },
      sparkline: { enabled: sparkline },
    },
    colors: colors ?? ['#0f5fd6'],
    plotOptions: {
      bar: {
        horizontal,
        columnWidth: '62%',
        borderRadius: 3,
        distributed,
        // Anchor the value at the END/TOP of the bar in both orientations.
        dataLabels: { position: 'top' },
      },
    },
    // Subtle value labels (bars only; the decorative sparkline stays clean).
    // White text sits INSIDE the colored fill in both orientations, with a
    // faint dark halo for legibility, anchored at the bar's end/top.
    // Two suppression rules keep them tidy: hide zero / rounds-to-zero values,
    // and hide any label that can't fully fit inside the bar — measured against
    // the bar LENGTH for horizontal, and against BOTH the bar width and height
    // for vertical — so a small bar never shows a number spilling past it.
    dataLabels: {
      enabled: type === 'bar' && !sparkline,
      // Horizontal: right-anchor the value just inside the bar's end tip.
      // Vertical: centered, nudged down so it clears the column's top edge.
      // The offset scales with the (zoom-aware) font so it stays proportional.
      textAnchor: horizontal ? 'end' : 'middle',
      offsetX: horizontal ? -labelOffsetPx : 0,
      offsetY: horizontal ? 0 : labelOffsetPx,
      formatter: (val: number, opts: any) => {
        if (val == null) return '';
        const label = valueFormatter ? valueFormatter(val) : `${val}`;
        // (2) Drop zero / rounds-to-zero labels (no non-zero digit is shown).
        if (!/[1-9]/.test(label)) return '';
        // (3) Hide if the label is wider than the bar can hold. Per-glyph width
        // tracks the current (zoom-aware) font size so the test stays honest.
        try {
          const g = opts?.w?.globals;
          if (g?.gridWidth) {
            const labelPx = label.length * glyphPx + 4;
            if (horizontal) {
              // Bar length in px ∝ |value| over the value-axis span.
              const range = g.maxY - g.minY;
              const barPx =
                range > 0 ? (Math.abs(val) / range) * g.gridWidth : Infinity;
              if (labelPx > barPx) return '';
            } else {
              // Vertical: the horizontal label sits inside the column near its
              // top, so it must fit the bar BOTH ways —
              //  • WIDTH : label text vs the actual bar width (62% of the slot).
              //  • HEIGHT: the bar (height ∝ |value|) must be tall enough to
              //    contain the label, else a short bar shows a label spilling
              //    past its top/bottom.
              const n =
                opts?.w?.config?.series?.[opts.seriesIndex]?.data?.length ||
                g.dataPoints ||
                1;
              const barWidthPx = (g.gridWidth / n) * 0.62; // matches columnWidth
              if (labelPx > barWidthPx) return '';
              const range = g.maxY - g.minY;
              const barHeightPx =
                range > 0 ? (Math.abs(val) / range) * g.gridHeight : Infinity;
              // Text dropped labelOffsetPx from the top needs that + the glyph
              // height (≈ font size) of bar to sit fully inside.
              if (barHeightPx < labelOffsetPx + labelFontPx + 2) return '';
            }
          }
        } catch (_e) {
          /* geometry unavailable — fall through and show the label */
        }
        return label;
      },
      style: {
        fontSize: `${labelFontPx}px`,
        fontWeight: 600,
        colors: ['#ffffff'],
      },
      dropShadow: {
        enabled: true,
        top: 0,
        left: 0,
        blur: 1,
        opacity: 0.35,
        color: '#000000',
      },
    },
    stroke:
      type === 'bar'
        ? { show: true, width: 1, colors: ['transparent'] }
        : { curve: 'smooth', width: 2 },
    fill:
      type === 'area'
        ? { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0 } }
        : {},
    // Single-series tiles stay legend-less (unchanged); multi-series tiles (e.g.
    // exposure + PnL) show a compact top legend so the bars are distinguishable.
    legend: { show: series.length > 1, position: 'top', fontSize: '11px' },
    grid: sparkline
      ? { show: false, padding: { top: 2, bottom: 2, left: 0, right: 0 } }
      : { padding: { top: 0, right: 8, bottom: 0, left: 8 } },
    // Decorative spark: no axes, no tooltip (the value is shown numerically
    // beside it, and a tooltip would be clipped inside the tiny container).
    tooltip: sparkline
      ? { enabled: false }
      : {
          y: {
            formatter: valueFormatter
              ? (v: number) => valueFormatter(v)
              : undefined,
          },
        },
    xaxis: sparkline
      ? {
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          crosshairs: { show: false },
          tooltip: { enabled: false },
        }
      : horizontal
        ? { ...catAxis, ...valueAxisForHorizontal(valueFormatter) }
        : catAxis,
    yaxis: sparkline ? { show: false } : horizontal ? {} : valueAxis,
  };

  // Attach these only when needed — passing `undefined` would clobber
  // ApexCharts' own defaults (it merges config over defaults) and crash on
  // `chart.events.beforeMount`.
  if (onBarClick) {
    options.chart.events = {
      dataPointSelection: (_e: any, _ctx: any, opts: any) =>
        onBarClick(opts.dataPointIndex),
    };
    options.states = { active: { filter: { type: 'none' } } };
  }

  return (
    <div style={{ height, ...style }}>
      <Chart options={options} series={series} type={type} height={height} />
    </div>
  );
};

// For horizontal bars the value sits on the x-axis.
const valueAxisForHorizontal = (fmt?: Fmt) => ({
  labels: {
    formatter: fmt ? (v: number) => fmt(v) : (v: number) => `${v}`,
    style: { fontSize: '11px' },
  },
});

export default MiniChart;
